import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Col,
  Row,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

export const getProgressColumns = ({ formatNumber, formatPercentage, showUomInHeaders = false, uom = '', compactLabels = false, labelsOverride = {}, isUnitsUom = false }) => {
  const grossTitle = showUomInHeaders && uom
    ? <>Total / Gross (<span className="uom-text">{String(uom).toLowerCase()}</span>)</>
    : 'Total / Gross';

  const label = (longLabel, shortLabel) => (compactLabels ? shortLabel : longLabel);
  const withUom = (content) => (
    showUomInHeaders && uom
      ? <>{content} (<span className="uom-text">{String(uom).toLowerCase()}</span>)</>
      : <>{content}</>
  );

  return [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      className: 'ps-col ps-col-desc',
      editable: true,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: grossTitle,
      dataIndex: 'grossTotal',
      key: 'grossTotal',
      className: 'ps-col ps-col-gross',
      editable: true,
      align: 'center',
      render: (grossTotal) => {
        if (grossTotal === undefined || grossTotal === null) return formatNumber(null);
        if (typeof grossTotal === 'object' && grossTotal !== null) {
          return grossTotal.uom === 'units'
            ? formatNumber(grossTotal.value)
            : `${formatNumber(grossTotal.value)} ${String(grossTotal.uom || '').toLowerCase()}`;
        }
        const numeric = Number(grossTotal) || 0;
        return formatNumber(numeric);
      },
    },
    {
      title: withUom(label('FAB.', 'FAB.')),
      dataIndex: 'fabrication',
      key: 'fabrication',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (fabrication) => {
        if (!fabrication) return formatNumber(null);
        return formatNumber(fabrication.totalGross);
      },
    },
    {
      title: '%',
      dataIndex: 'fabrication',
      key: 'fabricationPercentage',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (fabrication) => {
        if (!fabrication) return formatPercentage(null);
        return formatPercentage(fabrication.percentageCompleted);
      },
    },
    {
      key: 'space',
      className: 'ps-col ps-col-space',
    },
    {
      title: withUom(label('QC REL.', 'QC REL.')),
      dataIndex: 'qcRelease',
      key: 'qcRelease',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (qcRelease) => {
        if (!qcRelease) return formatNumber(null);
        return formatNumber(qcRelease.totalGross);
      },
    },
    {
      title: '%',
      dataIndex: 'qcRelease',
      key: 'qcReleasePercentage',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (qcRelease) => {
        if (!qcRelease) return formatPercentage(null);
        return formatPercentage(qcRelease.percentageCompleted);
      },
    },
    {
      key: 'space',
      className: 'ps-col ps-col-space',
    },
    {
      title: labelsOverride.corrisProtDelWrkshp
        ? withUom(labelsOverride.corrisProtDelWrkshp)
        : withUom(label(<span className="ps-allow-newline">{'CORR. PROT.\n/DEL. WRKP.'}</span>, 'CORROS. PROT.')),
      dataIndex: 'corrisProtDelWrkshp',
      key: 'corrisProtDelWrkshp',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (corrisProtDelWrkshp) => {
        if (!corrisProtDelWrkshp) return formatNumber(null);
        return formatNumber(corrisProtDelWrkshp.totalGross);
      },
    },
    {
      title: '%',
      dataIndex: 'corrisProtDelWrkshp',
      key: 'corrisProtDelWrkshpPercentage',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (corrisProtDelWrkshp) => {
        if (!corrisProtDelWrkshp) return formatPercentage(null);
        return formatPercentage(corrisProtDelWrkshp.percentageCompleted);
      },
    },
    {
      key: 'space',
      className: 'ps-col ps-col-space',
    },
    {
      title: withUom(label('DEL. SITE', 'DEL.')),
      dataIndex: 'delSite',
      key: 'delSite',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (delSite) => {
        if (!delSite) return formatNumber(null);
        return formatNumber(delSite.totalGross);
      },
    },
    {
      title: '%',
      dataIndex: 'delSite',
      key: 'delSitePercentage',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (delSite) => {
        if (!delSite) return formatPercentage(null);
        return formatPercentage(delSite.percentageCompleted);
      },
    },
    {
      key: 'space',
      className: 'ps-col ps-col-space',
    },
    {
      title: withUom(label(<span className="ps-allow-newline">{'SITE\nRECEIPT'}</span>, 'SITE RECPT.')),
      dataIndex: 'siteReceipt',
      key: 'siteReceipt',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (siteReceipt) => {
        if (!siteReceipt) return formatNumber(null);
        return formatNumber(siteReceipt.totalGross);
      },
    },
    {
      title: '%',
      dataIndex: 'siteReceipt',
      key: 'siteReceiptPercentage',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (siteReceipt) => {
        if (!siteReceipt) return formatPercentage(null);
        return formatPercentage(siteReceipt.percentageCompleted);
      },
    },
    {
      key: 'space',
      className: 'ps-col ps-col-space',
      ...(isUnitsUom && { width: '16%' }),
    },
    {
      title: withUom(label('INSTAL.', 'INSTAL.')),
      dataIndex: 'installation',
      key: 'installation',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (installation, record) => {
        if (record.description?.toLowerCase().includes('units')) {
          return null;
        }
        if (!installation) return formatNumber(null);
        return formatNumber(installation.totalGross);
      },
    },
    {
      title: '%',
      dataIndex: 'installation',
      key: 'installationPercentage',
      className: 'ps-col ps-col-stage',
      editable: true,
      align: 'center',
      render: (installation, record) => {
        if (record.description?.toLowerCase().includes('units')) {
          return null;
        }
        if (!installation) return formatPercentage(null);
        return formatPercentage(installation.percentageCompleted);
      },
    },
  ];
};

const ProgressSummaryReporting = ({ canUpdate, progressData, loading }) => {
  // Format numbers with comma as decimal separator and limit to one decimal place
  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0,0';

    // Convert to number to handle any string inputs
    const num = Number(value);

    // Check if it's a whole number
    if (Number.isInteger(num)) {
      // For whole numbers, add ',0' at the end
      return `${num},0`;
    } else {
      // For decimal numbers, keep only one decimal place and replace '.' with ','
      // First convert to string with one decimal place
      const formatted = num.toFixed(1);
      // Replace the decimal point with comma
      return formatted.replace('.', ',');
    }
  };

  // Format percentage values with comma as decimal separator
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0,00%';

    // Convert to number to handle any string inputs
    const num = Number(value);

    // For whole numbers with 3 digits or less (like 100), add ',00'
    if (Number.isInteger(num) && num.toString().length <= 3) {
      return `${num},00%`;
    } else {
      // For decimal numbers, keep two decimal places and replace '.' with ','
      const formatted = num.toFixed(2);
      // Replace the decimal point with comma
      return `${formatted.replace('.', ',')}%`;
    }
  };
  const [editingKey, setEditingKey] = useState("");
  const [data, setData] = useState([]);
  const [form] = Form.useForm();


  useEffect(() => {
    if (progressData) {
      const formattedData = formatProgressData(progressData);
      setData(formattedData);
    }
  }, [progressData]);

  // Transform API data into table-compatible format
  const formatProgressData = (data) => {
    return Object.keys(data).map((key, index) => {
      const item = data[key];
      return {
        id: index,
        position: index,
        description: `${key.toUpperCase()} - ${(item.uom || '').toLowerCase()}`,
        grossTotal: {
          value: item['Total / Gross'] || 0,
          uom: (item.uom || '').toLowerCase()
        },
        fabrication: {
          totalGross: item.fabrication?.totalGross || 0,
          percentageCompleted: item.fabrication?.percentageCompleted || 0
        },
        qcRelease: {
          totalGross: item.qc_release?.totalGross || 0,
          percentageCompleted: item.qc_release?.percentageCompleted || 0
        },
        corrisProtDelWrkshp: {
          totalGross: item.corrosion_protection?.totalGross || 0,
          percentageCompleted: item.corrosion_protection?.percentageCompleted || 0
        },
        delSite: {
          totalGross: item.delivery_to_site?.totalGross || 0,
          percentageCompleted: item.delivery_to_site?.percentageCompleted || 0
        },
        siteReceipt: {
          totalGross: item.site_receipt?.totalGross || 0,
          percentageCompleted: item.site_receipt?.percentageCompleted || 0
        },
        installation: {
          totalGross: item.installation?.totalGross || 0,
          percentageCompleted: item.installation?.percentageCompleted || 0
        }
      };
    });
  };

  // No longer needed as we're using 'Total / Gross' directly from the API
  // Keeping this comment for documentation purposes

  const columns = getProgressColumns({ formatNumber, formatPercentage, showUomInHeaders: false });

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        // title: col.title,
        // editing: isEditing(record),
      }),
    };
  });

  return (
    <Row className="production-manager-parent-row card-container progress-summary-card">
      <Col xxl={24} xl={24} lg={24} md={24} className="heading-col">
        <Row align="middle" gutter={[30, 0]}>
          <Col>
            <div>
              <span className="heading-span">SUMMARY</span>
            </div>
          </Col>
        </Row>
      </Col>

      <Col lg={24} xl={24} xxl={24} md={24}>
        <Form
          form={form}
          component={false}
          className="table-style"
        >
          <style>
            {`
              .ant-input {
                padding-left: 12px !important;
              }
              .ant-select-selector {
                padding-left: 12px !important;
              }
              .ant-select-selection-item {
                padding-left: 0px !important;
              }
              /* Target the actual input element inside ant-input */
              input.ant-input {
                text-indent: 8px;
              }
              .special-table {
                width: 100% !important;
              }
              .special-table .ant-table {
                width: 100% !important;
              }
              .special-table .ant-table-container {
                width: 100% !important;
              }
              .special-table .ant-table-content {
                width: 100% !important;
              }
              .special-table table {
                width: 100% !important;
              }
              /* Override card container border radius */
              .production-manager-parent-row.card-container {
                border-radius: 0 !important;
              }
              /* Add black line below the last row */
              .special-table .ant-table-tbody tr:last-child td {
                border-bottom: 0px solid #000 !important;
              }
              /* Add 1px solid border top for fabrication columns */
              .special-table .ant-table-thead > tr > th:nth-child(3),
              .special-table .ant-table-thead > tr > th:nth-child(4) {
                border-top: 1px solid #8d74b1 !important;
              }
              /* Add 1px solid border top for QC Release columns */
              .special-table .ant-table-thead > tr > th:nth-child(6),
              .special-table .ant-table-thead > tr > th:nth-child(7) {
                border-top: 1px solid #ab0800 !important;
              }
              /* Add 1px solid border top for CORRIS PROT/DEL. WRKSHP columns */
              .special-table .ant-table-thead > tr > th:nth-child(9),
              .special-table .ant-table-thead > tr > th:nth-child(10) {
                border-top: 1px solid #ffc000 !important;
              }
              /* Add 1px solid border top for DEL SITE columns */
              .special-table .ant-table-thead > tr > th:nth-child(12),
              .special-table .ant-table-thead > tr > th:nth-child(13) {
                border-top: 1px solid #00b0f0 !important;
              }
              /* Add 1px solid border top for SITE RECEIPT columns */
              .special-table .ant-table-thead > tr > th:nth-child(15),
              .special-table .ant-table-thead > tr > th:nth-child(16) {
                border-top: 1px solid #f5a44d !important;
              }
              /* Add 1px solid border top for INSTALLATION columns */
              .special-table .ant-table-thead > tr > th:nth-child(18),
              .special-table .ant-table-thead > tr > th:nth-child(19) {
                border-top: 1px solid #92d050 !important;
              }
              /* Add left border for fabrication column */
              .special-table .ant-table-thead > tr > th:nth-child(3),
              .special-table .ant-table-tbody > tr > td:nth-child(3) {
                border-left: 1px solid #8d74b1 !important;
              }
              /* Add left border for QC Release column */
              .special-table .ant-table-thead > tr > th:nth-child(6),
              .special-table .ant-table-tbody > tr > td:nth-child(6) {
                border-left: 1px solid #ab0800 !important;
              }
              /* Add left border for CORRIS PROT/DEL. WRKSHP column */
              .special-table .ant-table-thead > tr > th:nth-child(9),
              .special-table .ant-table-tbody > tr > td:nth-child(9) {
                border-left: 1px solid #ffc000 !important;
              }
              /* Add left border for DEL SITE column */
              .special-table .ant-table-thead > tr > th:nth-child(12),
              .special-table .ant-table-tbody > tr > td:nth-child(12) {
                border-left: 1px solid #00b0f0 !important;
              }
              /* Add left border for SITE RECEIPT column */
              .special-table .ant-table-thead > tr > th:nth-child(15),
              .special-table .ant-table-tbody > tr > td:nth-child(15) {
                border-left: 1px solid #f5a44d !important;
              }
              /* Add left border for INSTALLATION column */
              .special-table .ant-table-thead > tr > th:nth-child(18),
              .special-table .ant-table-tbody > tr > td:nth-child(18) {
                border-left: 1px solid #92d050 !important;
              }
              /* Add 8px border radius to top-left corner of fabrication column */
              .special-table .ant-table-thead > tr > th:nth-child(3) {
                border-top-left-radius: 8px !important;
              }
              /* Add 8px border radius to top-left corner of QC Release column */
              .special-table .ant-table-thead > tr > th:nth-child(6) {
                border-top-left-radius: 8px !important;
              }
              /* Add 8px border radius to top-left corner of CORRIS column */
              .special-table .ant-table-thead > tr > th:nth-child(9) {
                border-top-left-radius: 8px !important;
              }
              /* Add 8px border radius to top-left corner of DEL SITE column */
              .special-table .ant-table-thead > tr > th:nth-child(12) {
                border-top-left-radius: 8px !important;
              }
              /* Add 8px border radius to top-left corner of SITE RECEIPT column */
              .special-table .ant-table-thead > tr > th:nth-child(15) {
                border-top-left-radius: 8px !important;
              }
              /* Add 8px border radius to top-left corner of INSTALLATION column */
              .special-table .ant-table-thead > tr > th:nth-child(18) {
                border-top-left-radius: 8px !important;
              }
              /* Add right border to fabrication percentage column */
              .special-table .ant-table-thead > tr > th:nth-child(4),
              .special-table .ant-table-tbody > tr > td:nth-child(4) {
                border-right: 1px solid #8d74b1 !important;
              }
              /* Add right border to QC Release percentage column */
              .special-table .ant-table-thead > tr > th:nth-child(7),
              .special-table .ant-table-tbody > tr > td:nth-child(7) {
                border-right: 1px solid #ab0800 !important;
              }
              /* Add right border to CORRIS percentage column */
              .special-table .ant-table-thead > tr > th:nth-child(10),
              .special-table .ant-table-tbody > tr > td:nth-child(10) {
                border-right: 1px solid #ffc000 !important;
              }
              /* Add right border to DEL SITE percentage column */
              .special-table .ant-table-thead > tr > th:nth-child(13),
              .special-table .ant-table-tbody > tr > td:nth-child(13) {
                border-right: 1px solid #00b0f0 !important;
              }
              /* Add right border to SITE RECEIPT percentage column */
              .special-table .ant-table-thead > tr > th:nth-child(16),
              .special-table .ant-table-tbody > tr > td:nth-child(16) {
                border-right: 1px solid #f5a44d !important;
              }
              /* Add right border to INSTALLATION percentage column */
              .special-table .ant-table-thead > tr > th:nth-child(19),
              .special-table .ant-table-tbody > tr > td:nth-child(19) {
                border-right: 1px solid #92d050 !important;
              }
              /* Add top-right border radius */
              .special-table .ant-table-thead > tr > th:nth-child(4) {
                border-top-right-radius: 8px !important;
              }
              .special-table .ant-table-thead > tr > th:nth-child(7) {
                border-top-right-radius: 8px !important;
              }
              .special-table .ant-table-thead > tr > th:nth-child(10) {
                border-top-right-radius: 8px !important;
              }
              .special-table .ant-table-thead > tr > th:nth-child(13) {
                border-top-right-radius: 8px !important;
              }
              .special-table .ant-table-thead > tr > th:nth-child(16) {
                border-top-right-radius: 8px !important;
              }
              .special-table .ant-table-thead > tr > th:nth-child(19) {
                border-top-right-radius: 8px !important;
              }
              /* Add bottom border to the last row of fabrication columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(3),
              .special-table .ant-table-tbody tr:last-child td:nth-child(4) {
                border-bottom: 1px solid #8d74b1 !important;
              }
              /* Add bottom border to the last row of QC Release columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(6),
              .special-table .ant-table-tbody tr:last-child td:nth-child(7) {
                border-bottom: 1px solid #ab0800 !important;
              }
              /* Add bottom border to the last row of CORRIS columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(9),
              .special-table .ant-table-tbody tr:last-child td:nth-child(10) {
                border-bottom: 1px solid #ffc000 !important;
              }
              /* Add bottom border to the last row of DEL SITE columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(12),
              .special-table .ant-table-tbody tr:last-child td:nth-child(13) {
                border-bottom: 1px solid #00b0f0 !important;
              }
              /* Add bottom border to the last row of SITE RECEIPT columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(15),
              .special-table .ant-table-tbody tr:last-child td:nth-child(16) {
                border-bottom: 1px solid #f5a44d !important;
              }
              /* Add bottom border to the last row of INSTALLATION columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(18),
              .special-table .ant-table-tbody tr:last-child td:nth-child(19) {
                border-bottom: 1px solid #92d050 !important;
              }
              /* Add 8px border radius to bottom corners of fabrication columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(3) {
                border-bottom-left-radius: 8px !important;
              }
              .special-table .ant-table-tbody tr:last-child td:nth-child(4) {
                border-bottom-right-radius: 8px !important;
              }
              /* Add 8px border radius to bottom corners of QC Release columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(6) {
                border-bottom-left-radius: 8px !important;
              }
              .special-table .ant-table-tbody tr:last-child td:nth-child(7) {
                border-bottom-right-radius: 8px !important;
              }
              /* Add 8px border radius to bottom corners of CORRIS columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(9) {
                border-bottom-left-radius: 8px !important;
              }
              .special-table .ant-table-tbody tr:last-child td:nth-child(10) {
                border-bottom-right-radius: 8px !important;
              }
              /* Add 8px border radius to bottom corners of DEL SITE columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(12) {
                border-bottom-left-radius: 8px !important;
              }
              .special-table .ant-table-tbody tr:last-child td:nth-child(13) {
                border-bottom-right-radius: 8px !important;
              }
              /* Add 8px border radius to bottom corners of SITE RECEIPT columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(15) {
                border-bottom-left-radius: 8px !important;
              }
              .special-table .ant-table-tbody tr:last-child td:nth-child(16) {
                border-bottom-right-radius: 8px !important;
              }
                /* Add 8px border radius to bottom corners of INSTALLATION columns */
              .special-table .ant-table-tbody tr:last-child td:nth-child(18) {
                border-bottom-left-radius: 8px !important;
              }
              .special-table .ant-table-tbody tr:last-child td:nth-child(19) {
                border-bottom-right-radius: 8px !important;
              }
              /* Set padding to zero for space columns */
              .special-table .ant-table-thead > tr > th:nth-child(5),
              .special-table .ant-table-thead > tr > th:nth-child(8),
              .special-table .ant-table-thead > tr > th:nth-child(11),
              .special-table .ant-table-thead > tr > th:nth-child(14),
              .special-table .ant-table-thead > tr > th:nth-child(17),
              .special-table .ant-table-thead > tr > th:nth-child(20),
              .special-table .ant-table-tbody > tr > td:nth-child(5),
              .special-table .ant-table-tbody > tr > td:nth-child(8),
              .special-table .ant-table-tbody > tr > td:nth-child(11),
              .special-table .ant-table-tbody > tr > td:nth-child(14),
              .special-table .ant-table-tbody > tr > td:nth-child(17),
              .special-table .ant-table-tbody > tr > td:nth-child(20) {
                padding: 0 !important;
              }
            `}
          </style>
          <Table
            className="special-table activity-type-table project-statuses-table"
            rowClassName="editable-row"
            dataSource={data}
            loading={loading}
            columns={mergedColumns}
            pagination={false}
            rowKey="position"
            style={{ width: '100%' }}
          />
          <Col lg={24} xl={24} xxl={24} md={24} className="table">
            {canUpdate && (
              <Button
                icon={<PlusOutlined />}
                className="add-user-btn"
                size="middle"
                // disabled={isAdding}
                style={{
                  marginTop: "20px",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                TYPE
              </Button>
            )}
          </Col>
        </Form>
      </Col>
    </Row>
  );
};

export default ProgressSummaryReporting;